import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { observer } from 'mobx-react-lite'
import { NodeProps } from 'reactflow'
import { BAY_WIDTH, ROW_HEIGHT } from '../../helpers/yard-view.utils'
import { FlexibleBlockWithOccupanciesAndActions } from '../../models/yard-view.model'
import { BlockColorDistributionBar } from '../atoms/BlockColorDistributionBar'
import { FlexibleBlockHeader } from '../molecules/FlexibleBlockHeader'
import { FocusWrapper } from '../molecules/FocusWrapper'
import { RotatableNodeWrapper } from '../molecules/RotatableNodeWrapper'

export const FlexibleBlockNode = observer(
  ({ data }: NodeProps<FlexibleBlockWithOccupanciesAndActions>) => {
    const {
      id,
      name,
      focused,
      showNodeHeaders,
      isEditable,
      occupancies,
      layoutPosition,
      equipmentTypes,
      distribution,
      isRestSelected,
      onFocus,
      onEdit,
      onDelete,
      onDoubleClick,
      onRotate,
    } = data
    const { t } = useTranslate()

    // Show the bar only when at least one selected value has matching containers
    // in this block. Empty blocks, an inactive scheme, no selected values, or a
    // block whose containers all fall into "Other" each leave segments empty and
    // suppress the bar — the count text always stays visible regardless.
    const showBar = distribution.segments.length > 0
    const percent = (count: number) =>
      Math.round((count / distribution.totalCount) * 100)

    const tooltipBody = showBar ? (
      <Stack spacing={0.5}>
        {distribution.segments.map(s => (
          <Typography key={s.value} variant='caption' whiteSpace='nowrap'>
            {`${s.label} · ${s.count} · ${percent(s.count)}%`}
          </Typography>
        ))}
        {distribution.otherCount > 0 && (
          <Typography variant='caption' whiteSpace='nowrap'>
            {`${t('flexibleBlockDistributionOther', 'Other')} · ${distribution.otherCount} · ${percent(distribution.otherCount)}%`}
          </Typography>
        )}
      </Stack>
    ) : null

    return (
      <RotatableNodeWrapper
        rotation={layoutPosition.rotation}
        enabled={focused && isEditable}
        onRotationEnd={angle => onRotate(id, angle)}
        minHeaderSpacing={60}
        header={
          focused || showNodeHeaders ? (
            <FlexibleBlockHeader
              id={id}
              name={name}
              focused={focused}
              equipmentTypes={equipmentTypes}
              isEditable={isEditable}
              hasOccupancies={occupancies.length > 0}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ) : undefined
        }
      >
        <FocusWrapper
          focused={focused}
          isEditable={isEditable}
          onFocus={() => onFocus(id)}
          height={ROW_HEIGHT * 3}
          width={BAY_WIDTH * 3}
          onDoubleClick={() => onDoubleClick(id)}
        >
          <Stack spacing={1} sx={{ width: '100%', px: 2 }}>
            <Typography variant='body1'>
              {t('containers', 'Containers')}: {occupancies.length}
            </Typography>
            {showBar && (
              <Tooltip title={tooltipBody} placement='top'>
                <Box sx={{ width: '100%' }}>
                  <BlockColorDistributionBar
                    segments={distribution.segments}
                    otherCount={distribution.otherCount}
                    totalCount={distribution.totalCount}
                    isRestSelected={isRestSelected}
                  />
                </Box>
              </Tooltip>
            )}
          </Stack>
        </FocusWrapper>
      </RotatableNodeWrapper>
    )
  },
)
